/**
 * web/src/components/gates/EmailBodyEditor.tsx
 * Rich-text email body editor for the email-gate review (TipTap/ProseMirror).
 * Replaces the bare contenteditable div (2026-06 testing-week feedback: the
 * reviewer rejected the welcome email because they couldn't tell what was a
 * link or add/edit one). Toolbar: bold / italic / underline / bullet list /
 * numbered list + a link popover that adds, edits, and removes hyperlinks on
 * the selection.
 *
 * Gmail-style link bubble: clicking inside a link (openOnClick is false, so
 * a click just places the caret) pops a floating bubble showing the target
 * URL — clickable to open in a new tab — with Change / Remove actions, so a
 * reviewer can verify where "HERE" actually points without leaving the
 * editor.
 *
 * The editor's tag vocabulary intentionally matches what the Google Docs
 * template walker emits (`<p>`, `<ul>`/`<ol>` `<li>`, `<strong>`, `<em>`,
 * `<u>`, `<a href>`, `<br>`) so a Doc-sourced body round-trips losslessly —
 * StarterKit's heading / blockquote / code / codeBlock / horizontalRule
 * nodes are disabled to keep the output inside the email-safe set that
 * `email_step.html_to_text` lowers for the text/plain MIME part.
 *
 * Ordered lists: StarterKit's orderedList natively parses + renders BOTH the
 * `start=` and `type=` attributes (verified against @tiptap/extension-list
 * 3.25 in EmailBodyEditor-orderedListType.test.ts), so a Doc's lettered/roman
 * sub-list (`<ol type="a">`) keeps its a./b. markers through reviewer edits and
 * reaches the sponsor as authored. The `[&_ol[type=…]]` CSS below makes THIS
 * preview render the same marker (the blanket `list-decimal` would otherwise
 * show 1./2. even though the wire HTML — and the sponsor's client — show a./b.).
 *
 * SECURITY (F24): both sinks still run through sanitizeEmailHtml (DOMPurify) —
 * the controlled-prop seed before setContent, and getHTML() read-back before
 * it reaches the form — so neither a hostile template value nor pasted markup
 * can persist scripts into an authenticated reviewer session.
 */
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Icon, type IconName } from '../../lib/icons';
import { sanitizeEmailHtml } from '../../lib/sanitize';

/** Public props — same controlled contract as the old HtmlBodyEditor. */
export interface EmailBodyEditorProps {
  /** Current HTML value (RHF-controlled). */
  html: string;
  /** Called with sanitized HTML on every edit. */
  onChange: (next: string) => void;
  ariaInvalid?: boolean;
}

/**
 * Controlled TipTap editor for the email body. Initializes from `html` and
 * only re-sets content when the value changes from OUTSIDE (form reset after
 * draft restore, regenerate reseed) — local typing flows out through
 * onUpdate so the cursor position is preserved.
 *
 * @param html controlled HTML value
 * @param onChange receives sanitized HTML on each edit
 * @param ariaInvalid forwarded to the contenteditable for a11y
 * @sideEffects creates a ProseMirror editor instance (destroyed on unmount)
 * @idempotent yes — same props converge on the same rendered document
 */
export function EmailBodyEditor({ html, onChange, ariaInvalid }: EmailBodyEditorProps) {
  // Tracks the last HTML we know is in sync with the editor. Differs from
  // `html` when the parent pushes a new value from outside.
  const lastSynced = useRef<string>('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Keep the schema inside the email-safe tag set (module docstring).
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: {
          // Editing UX: clicking a link places the cursor instead of
          // navigating; the toolbar popover owns open/edit/remove.
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          defaultProtocol: 'https',
          // TipTap's defaults add rel/target noise that means nothing in an
          // email body — strip so the wire HTML stays `<a href>` only.
          HTMLAttributes: { rel: null, target: null },
        },
      }),
    ],
    // Seeded by the sync effect below (which also handles the first render),
    // so the sanitizer runs on exactly one path.
    content: '',
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Email body',
        class: cn(
          'min-h-[320px] w-full bg-transparent border-0 outline-none rounded-md',
          // Font, line height and block gaps mirror the inline styles the
          // server stamps on the sent copy (pixel.email.html_layout), so what
          // the reviewer sees here is what the sponsor's mail client shows.
          'text-text font-[Arial,Helvetica,sans-serif] text-[14px] leading-[1.5]',
          '[&_p]:mt-0 [&_p]:mb-[1em] [&_ul]:mt-0 [&_ul]:mb-[1em] [&_ul]:pl-6 [&_ul]:list-disc',
          '[&_ol]:mt-0 [&_ol]:mb-[1em] [&_ol]:pl-6 [&_ol]:list-decimal',
          '[&_li_ul]:mb-0 [&_li_ol]:mb-0 [&_li_p]:mb-0',
          // Honour <ol type=…> in the preview so a lettered/roman sub-list
          // renders the SAME marker the sponsor will see (not decimal).
          "[&_ol[type='a']]:list-[lower-alpha] [&_ol[type='A']]:list-[upper-alpha]",
          "[&_ol[type='i']]:list-[lower-roman] [&_ol[type='I']]:list-[upper-roman]",
          // Links must READ as links while editing (testing-week feedback) —
          // accent color + underline, same tokens as the old preview styling.
          // cursor-pointer so link text feels interactive — clicking opens the
          // link bubble (caret placement + URL/Change/Remove), like Gmail.
          '[&_a]:text-accent [&_a]:underline [&_a]:underline-offset-2 [&_a]:cursor-pointer',
          '[&_strong]:font-semibold',
        ),
      },
    },
    onUpdate: ({ editor: e }) => {
      const next = sanitizeEmailHtml(e.getHTML());
      lastSynced.current = next;
      onChange(next);
    },
  });

  // External-value sync: form reset (draft restore) / regenerate reseed push a
  // new `html`; our own onChange echoes back the value we just emitted (equal
  // to lastSynced, so no reset → cursor preserved).
  useEffect(() => {
    if (!editor) return;
    if (html !== lastSynced.current) {
      editor.commands.setContent(sanitizeEmailHtml(html), { emitUpdate: false });
      lastSynced.current = html;
    }
  }, [editor, html]);

  // Subscribe to just the toolbar-relevant state so buttons re-render on
  // selection/mark changes (v3 editors don't re-render the host component).
  const toolbar = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e?.isActive('bold') ?? false,
      italic: e?.isActive('italic') ?? false,
      underline: e?.isActive('underline') ?? false,
      bulletList: e?.isActive('bulletList') ?? false,
      orderedList: e?.isActive('orderedList') ?? false,
      link: e?.isActive('link') ?? false,
      linkHref: (e?.getAttributes('link').href as string | undefined) ?? '',
      hasSelection: e ? !e.state.selection.empty : false,
    }),
  });

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Link bubble (Gmail-style "Go to link / Change / Remove"). `bubbleEditing`
  // swaps the bubble's URL display for an inline edit input; reset whenever
  // the caret leaves the link so a stale edit state can't reopen later.
  const [bubbleEditing, setBubbleEditing] = useState(false);
  const [bubbleUrl, setBubbleUrl] = useState('');
  useEffect(() => {
    if (!toolbar?.link) setBubbleEditing(false);
  }, [toolbar?.link]);

  const openLinkEditor = () => {
    setLinkUrl(toolbar?.linkHref ?? '');
    setLinkOpen(true);
  };

  const applyLink = () => {
    if (!editor) return;
    const href = normalizeUrl(linkUrl);
    if (!href) {
      // Empty URL = remove the link (same as the unlink affordance).
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (toolbar?.link || toolbar?.hasSelection) {
      // Link the selection (or retarget the link under the cursor).
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    } else {
      // No selection: insert the URL itself as linked text.
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] })
        .run();
    }
    setLinkOpen(false);
  };

  const removeLink = () => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
    setBubbleEditing(false);
  };

  const applyBubbleUrl = () => {
    if (!editor) return;
    const href = normalizeUrl(bubbleUrl);
    if (!href) {
      removeLink();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    setBubbleEditing(false);
  };

  return (
    <div aria-invalid={ariaInvalid}>
      <div className="flex items-center gap-1 flex-wrap mb-2 pb-2 border-b border-border">
        <ToolbarButton
          icon="bold"
          label="Bold"
          active={toolbar?.bold}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          icon="italic"
          label="Italic"
          active={toolbar?.italic}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          icon="underline"
          label="Underline"
          active={toolbar?.underline}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        />
        <ToolbarButton
          icon="list"
          label="Bullet list"
          active={toolbar?.bulletList}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          icon="list-ordered"
          label="Numbered list"
          active={toolbar?.orderedList}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        />
        <span className="w-px h-4 bg-border mx-1" aria-hidden />
        <ToolbarButton
          icon="link"
          label={toolbar?.link ? 'Edit link' : 'Add link'}
          active={toolbar?.link}
          onClick={openLinkEditor}
        />
        {toolbar?.link && (
          <ToolbarButton icon="unlink" label="Remove link" onClick={removeLink} />
        )}
      </div>

      {linkOpen && (
        <div className="flex items-center gap-2 mb-2 p-2 rounded-md border border-border bg-bg-soft">
          <Icon name="link" size={13} className="shrink-0 text-text-muted" />
          <input
            autoFocus
            type="text"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault(); // don't submit the surrounding gate form
                applyLink();
              } else if (e.key === 'Escape') {
                e.stopPropagation();
                setLinkOpen(false);
              }
            }}
            placeholder="https://example.com"
            aria-label="Link URL"
            className="flex-1 min-w-0 bg-bg border border-border rounded-md px-2 py-1 text-[12px] text-text outline-none focus-visible:border-accent"
          />
          <TextButton onClick={applyLink}>
            {toolbar?.link ? 'Update' : 'Add link'}
          </TextButton>
          <TextButton onClick={() => setLinkOpen(false)}>Cancel</TextButton>
        </div>
      )}

      {editor && (
        <BubbleMenu
          editor={editor}
          pluginKey="emailLinkBubble"
          shouldShow={({ editor: e }) => e.isActive('link')}
          className={cn(
            'z-30 flex items-center gap-2 rounded-md border border-border bg-bg px-2.5 py-1.5',
            'shadow-md max-w-[420px]',
          )}
        >
          {bubbleEditing ? (
            <>
              <Icon name="link" size={13} className="shrink-0 text-text-muted" />
              <input
                autoFocus
                type="text"
                value={bubbleUrl}
                onChange={(e) => setBubbleUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // don't submit the surrounding gate form
                    applyBubbleUrl();
                  } else if (e.key === 'Escape') {
                    e.stopPropagation();
                    setBubbleEditing(false);
                  }
                }}
                placeholder="https://example.com"
                aria-label="Link URL"
                className="w-[240px] min-w-0 bg-bg border border-border rounded-md px-2 py-1 text-[12px] text-text outline-none focus-visible:border-accent"
              />
              <TextButton onClick={applyBubbleUrl}>Update</TextButton>
              <TextButton onClick={() => setBubbleEditing(false)}>Cancel</TextButton>
            </>
          ) : (
            <>
              <Icon name="link" size={13} className="shrink-0 text-text-muted" />
              <a
                href={toolbar?.linkHref || undefined}
                target="_blank"
                rel="noopener noreferrer"
                title={toolbar?.linkHref}
                className="truncate text-[12px] text-accent underline underline-offset-2 hover:text-accent-hover"
              >
                {toolbar?.linkHref}
              </a>
              <span className="w-px h-4 bg-border shrink-0" aria-hidden />
              <TextButton
                onClick={() => {
                  setBubbleUrl(toolbar?.linkHref ?? '');
                  setBubbleEditing(true);
                }}
              >
                Change
              </TextButton>
              <TextButton onClick={removeLink}>Remove</TextButton>
            </>
          )}
        </BubbleMenu>
      )}
      <EditorContent editor={editor} spellCheck />
    </div>
  );
}

/**
 * Normalizes a reviewer-typed URL: trims, and prefixes https:// when no
 * scheme is present (so "pixel.events/welcome" becomes a working link).
 * Returns '' for blank input. javascript:/data: are stripped later by
 * DOMPurify on read-back, so this stays a UX nicety, not a security gate.
 *
 * @param raw the reviewer-typed URL
 * @returns a usable href or ''
 * @sideEffects none
 * @idempotent yes
 */
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** Internal: icon toolbar button (type=button so it never submits the gate form). */
function ToolbarButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center h-7 w-7 rounded-md border text-text-muted',
        'hover:text-text hover:bg-bg-soft transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-1',
        active ? 'border-accent text-accent bg-bg-soft' : 'border-transparent',
      )}
    >
      <Icon name={icon} size={14} />
    </button>
  );
}

/** Internal: small text button for the link popover row. */
function TextButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 text-[12px] font-medium text-accent hover:text-accent-hover px-1.5 py-1"
    >
      {children}
    </button>
  );
}
